<template>
  <div class="admin-users-container">
    <el-container>
      <el-header>
        <div class="header-content">
          <div class="header-left">
            <el-button type="text" @click="goBack">
              <el-icon><arrow-left /></el-icon>
              返回
            </el-button>
            <h1>用户管理</h1>
          </div>
          <div class="header-right">
            <el-button type="primary" @click="showAddDialog = true">
              <el-icon><plus /></el-icon>
              添加用户
            </el-button>
          </div>
        </div>
      </el-header>

      <el-main>
        <!-- 搜索和筛选 -->
        <div class="filter-bar">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索用户名或邮箱"
            prefix-icon="search"
            style="width: 300px"
            @keyup.enter="fetchUsers"
          />
          <el-button type="primary" @click="fetchUsers">
            搜索
          </el-button>
          <el-button @click="resetFilter">
            重置
          </el-button>
        </div>

        <!-- 用户表格 -->
        <el-table
          v-loading="loading"
          :data="users"
          style="width: 100%"
          border
        >
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="username" label="用户名" />
          <el-table-column prop="email" label="邮箱" />
          <el-table-column prop="is_active" label="状态" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.is_active ? 'success' : 'danger'">
                {{ scope.row.is_active ? '激活' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="is_admin" label="角色" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.is_admin ? 'warning' : ''">
                {{ scope.row.is_admin ? '管理员' : '普通用户' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="created_at" label="创建时间" width="180">
            <template #default="scope">
              {{ formatDate(scope.row.created_at) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button
                type="text"
                size="small"
                @click="editUser(scope.row)"
              >
                编辑
              </el-button>
              <el-button
                type="text"
                size="small"
                :disabled="scope.row.id === currentUserId"
                @click="toggleUserStatus(scope.row)"
              >
                {{ scope.row.is_active ? '禁用' : '启用' }}
              </el-button>
              <el-button
                type="text"
                size="small"
                :disabled="scope.row.id === currentUserId"
                @click="deleteUser(scope.row.id)"
                style="color: #F56C6C"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <el-pagination
          v-if="total > 0"
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />

        <!-- 空状态 -->
        <el-empty v-if="users.length === 0 && !loading" description="暂无用户" />
      </el-main>
    </el-container>

    <!-- 添加/编辑用户对话框 -->
    <el-dialog
      v-model="showAddDialog"
      :title="editingUser ? '编辑用户' : '添加用户'"
      width="500px"
    >
      <el-form
        ref="userForm"
        :model="userForm"
        :rules="userRules"
        label-width="100px"
      >
        <el-form-item label="用户名" prop="username">
          <el-input v-model="userForm.username" placeholder="请输入用户名" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="userForm.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item :label="editingUser ? '新密码' : '密码'" prop="password">
          <el-input
            v-model="userForm.password"
            type="password"
            :placeholder="editingUser ? '留空则不修改密码' : '请输入密码'"
          />
        </el-form-item>
        <el-form-item label="状态" prop="is_active">
          <el-switch v-model="userForm.is_active" />
          <span style="margin-left: 10px">
            {{ userForm.is_active ? '激活' : '禁用' }}
          </span>
        </el-form-item>
        <el-form-item label="角色" prop="is_admin">
          <el-switch v-model="userForm.is_admin" />
          <span style="margin-left: 10px">
            {{ userForm.is_admin ? '管理员' : '普通用户' }}
          </span>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showAddDialog = false">取消</el-button>
          <el-button type="primary" @click="saveUser">
            {{ editingUser ? '更新' : '添加' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '../../api'

export default {
  name: 'AdminUsers',
  data() {
    return {
      users: [],
      loading: false,
      searchKeyword: '',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      showAddDialog: false,
      editingUser: null,
      userForm: {
        username: '',
        email: '',
        password: '',
        is_active: true,
        is_admin: false
      },
      userRules: {
        username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
        ],
        password: [
          {
            required: true,
            message: '请输入密码',
            trigger: 'blur',
            validator: (rule, value, callback) => {
              if (!this.editingUser && !value) {
                callback(new Error('请输入密码'))
              } else {
                callback()
              }
            }
          }
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['currentUser']),
    currentUserId() {
      return this.currentUser.id || 0
    }
  },
  mounted() {
    this.fetchUsers()
  },
  methods: {
    async fetchUsers() {
      this.loading = true
      try {
        const params = {
          skip: (this.currentPage - 1) * this.pageSize,
          limit: this.pageSize
        }
        if (this.searchKeyword) {
          // 注意：后端需要支持搜索，这里简化处理
        }
        const data = await api.getUsers(params)
        this.users = data
        this.total = data.length // 实际应返回总数
      } catch (error) {
        console.error('获取用户列表失败:', error)
        this.$message.error('获取用户列表失败')
      } finally {
        this.loading = false
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('zh-CN')
    },
    editUser(user) {
      this.editingUser = user
      this.userForm = {
        username: user.username,
        email: user.email,
        password: '',
        is_active: user.is_active,
        is_admin: user.is_admin
      }
      this.showAddDialog = true
    },
    async toggleUserStatus(user) {
      try {
        await this.$confirm(
          `确定${user.is_active ? '禁用' : '启用'}用户 "${user.username}" 吗？`,
          '提示',
          { type: 'warning' }
        )

        await api.updateUser(user.id, {
          is_active: !user.is_active
        })

        this.$message.success('操作成功')
        this.fetchUsers()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('操作失败:', error)
          this.$message.error('操作失败')
        }
      }
    },
    async deleteUser(userId) {
      try {
        await this.$confirm('确定删除此用户吗？', '提示', {
          type: 'warning'
        })

        await api.deleteUser(userId)
        this.$message.success('删除成功')
        this.fetchUsers()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }
    },
    async saveUser() {
      this.$refs.userForm.validate(async (valid) => {
        if (!valid) return

        try {
          const userData = { ...this.userForm }
          if (!userData.password && this.editingUser) {
            delete userData.password
          }

          if (this.editingUser) {
            await api.updateUser(this.editingUser.id, userData)
            this.$message.success('用户更新成功')
          } else {
            await api.createUser(userData)
            this.$message.success('用户添加成功')
          }

          this.showAddDialog = false
          this.resetUserForm()
          this.fetchUsers()
        } catch (error) {
          console.error('保存失败:', error)
          this.$message.error(error.response?.data?.detail || '保存失败')
        }
      })
    },
    resetUserForm() {
      this.editingUser = null
      this.userForm = {
        username: '',
        email: '',
        password: '',
        is_active: true,
        is_admin: false
      }
      this.$refs.userForm?.resetFields()
    },
    resetFilter() {
      this.searchKeyword = ''
      this.currentPage = 1
      this.fetchUsers()
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.fetchUsers()
    },
    handleCurrentChange(page) {
      this.currentPage = page
      this.fetchUsers()
    },
    goBack() {
      this.$router.push('/books')
    }
  }
}
</script>

<style scoped>
.admin-users-container {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-left h1 {
  margin: 0;
  color: white;
  font-size: 20px;
}

.el-header {
  background-color: #409EFF;
  padding: 0 20px;
}

.filter-bar {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
  align-items: center;
}

.el-pagination {
  margin-top: 20px;
  justify-content: center;
}
</style>